/**
 * Help-flow registry — mirrors src/data/walkthroughs/index.js.
 *
 * To add a flow:
 *   1. Create src/data/helpFlows/{flowId}.js exporting a flow object (§4 of
 *      docs/INTERACTIVE_HELP_PLAN.md).
 *   2. Import + register it below.
 *   3. Add its anchors to the inventory table in that doc (§5.2) and to the
 *      real JSX as `data-help-id` attributes.
 *
 * Gating is modular: each flow carries its own `minRoleWeight` and optional
 * `available(ctx)` predicate. The registry adds no cross-flow rules.
 */

import relocateResident from './relocateResident.js';
import moveInResident from './moveInResident.js';
import moveOutResident from './moveOutResident.js';
import assignPendant from './assignPendant.js';

/* The three lifecycle tasks exist twice: once for campuses where the portal owns
   resident data, once for campuses where an EHR does and the work is approving the
   task it sent (Decision 2 — this is what the reserved "Manage EHR tasks"
   placeholder became, three flows rather than one, because the user picks a task
   and not a mechanism).

   Each pair carries exactly complementary `available` gates on `ctx.ehrActive`, so
   precisely one of the two is offered and the picker always lists four tasks with
   no duplicate titles. Adding a weight override to either side would break that —
   see the comment in moveInResident.js. assignPendant has no variant: an EHR drives
   resident records, not device assignment, so it is the portal's job either way. */
import moveInResidentEhr from './moveInResidentEhr.js';
import moveOutResidentEhr from './moveOutResidentEhr.js';
import relocateResidentEhr from './relocateResidentEhr.js';

/* Picker order is deliberate: the resident lifecycle in the order a community
   experiences it, rather than alphabetically. assignPendant comes after move-in
   because assigning a pendant is what you usually do next; it sits before
   move-out/relocate so the two lifecycle-ending flows stay together. Each EHR
   variant sits immediately beside its portal counterpart so the order a user sees
   is identical on both kinds of campus. */
const FLOWS = [
    moveInResident,
    moveInResidentEhr,
    assignPendant,
    moveOutResident,
    moveOutResidentEhr,
    relocateResident,
    relocateResidentEhr,
];

/**
 * Flows the current user can actually run, given role + campus context.
 *
 * @param {{ roleWeight: number, ehrActive: boolean, campusConfig: object, portalVersion: string }} ctx
 * @returns {Array<object>}
 */
export function getAvailableFlows(ctx) {
    return FLOWS.filter(
        (f) => ctx.roleWeight >= (f.minRoleWeight ?? 0) && (f.available?.(ctx) ?? true),
    );
}

export function getFlow(id) {
    return FLOWS.find((f) => f.id === id) ?? null;
}

/** Every registered flow, ignoring gating — for the dev-only anchor audit. */
export function allFlows() {
    return FLOWS;
}
